import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { validateFiles, scanMessageText } from '../../lib/security/fileSecurity';
import { Coffee } from 'lucide-react';

const API     = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';
const CHAT_API = `${API}/customer/chat`;

const SEND_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);
const CLOSE_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const MIN_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const END_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const BACK_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="17" height="17">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
function AgentAvatarBtn({ isDark }) {
  return (
    <div style={{
      width: '100%', height: '100%', borderRadius: '50%',
      background: isDark ? 'rgba(10,186,181,0.18)' : 'rgba(10,186,181,0.22)',
      backdropFilter: 'blur(12px) saturate(1.6)',
      WebkitBackdropFilter: 'blur(12px) saturate(1.6)',
      border: isDark ? '1.5px solid rgba(10,186,181,0.45)' : '1.5px solid rgba(10,186,181,0.5)',
      boxShadow: isDark
        ? 'inset 0 1px 1px rgba(255,255,255,0.12), 0 2px 12px rgba(10,186,181,0.25)'
        : 'inset 0 1px 1px rgba(255,255,255,0.6),  0 2px 12px rgba(10,186,181,0.2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden',
    }}>
      {(() => {
        const ic = isDark ? 'rgba(255,255,255,0.92)' : '#0c4a6e';
        const ic2 = isDark ? 'rgba(255,255,255,0.78)' : '#0c4a6e';
        return (
          <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="86%" height="86%">
            <path d="M10 62 Q10 44 32 44 Q54 44 54 62" fill={ic}/>
            <circle cx="32" cy="25" r="11" fill={ic}/>
            <path d="M19 24 Q19 10 32 10 Q45 10 45 24" stroke={ic} strokeWidth="2.8" fill="none" strokeLinecap="round"/>
            <rect x="15" y="22" width="5" height="9" rx="2.5" fill={ic}/>
            <rect x="44" y="22" width="5" height="9" rx="2.5" fill={ic}/>
            <path d="M17.5 31 Q15 37 19 39" stroke={ic2} strokeWidth="2" fill="none" strokeLinecap="round"/>
            <circle cx="19.5" cy="39.5" r="2" fill={ic2}/>
          </svg>
        );
      })()}
    </div>
  );
}
const AGENT_AVATAR = (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
    <defs>
      <linearGradient id="ag" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0ABAB5"/>
        <stop offset="1" stopColor="#0369a1"/>
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="32" fill="url(#ag)"/>
    <path d="M10 62 Q10 44 32 44 Q54 44 54 62" fill="rgba(255,255,255,0.88)"/>
    <circle cx="32" cy="25" r="11" fill="rgba(255,255,255,0.92)"/>
    <path d="M19 24 Q19 10 32 10 Q45 10 45 24" stroke="white" strokeWidth="2.8" fill="none" strokeLinecap="round"/>
    <rect x="15" y="22" width="5" height="9" rx="2.5" fill="white"/>
    <rect x="44" y="22" width="5" height="9" rx="2.5" fill="white"/>
    <path d="M17.5 31 Q15 37 19 39" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/>
    <circle cx="19.5" cy="39.5" r="2" fill="white"/>
  </svg>
);
const CHECK_ONE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="14" height="14">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const CHECK_TWO = (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="3" width="17" height="17">
    <polyline points="17 6 9 14 6 11" stroke="currentColor"/>
    <polyline points="22 6 13 15 11 13" stroke="currentColor"/>
  </svg>
);
const ATTACH_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
  </svg>
);
const FILE_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
  </svg>
);

const URL_REGEX = /(https?:\/\/[^\s"'<>]+)/gi;

function renderText(msg) {
  if (!msg) return null;
  const parts = msg.split(URL_REGEX);
  return parts.map((part, i) =>
    URL_REGEX.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color:'#38bdf8', textDecoration:'underline', wordBreak:'break-all' }}>{part}</a>
      : part
  );
}

function renderAttachments(atts, onPreview) {
  if (!atts || atts.length === 0) return null;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem', marginTop:'0.3rem' }}>
      {atts.map((att, i) => {
        const isImg = att.type?.startsWith('image/');
        if (isImg) return (
          <div key={i} style={{ position:'relative', cursor:'zoom-in', display:'inline-block' }} onClick={() => onPreview(att)}>
            <img src={att.data_url} alt={att.name} style={{ maxWidth:'100%', maxHeight:160, borderRadius:'0.5rem', display:'block', objectFit:'cover', border:'1px solid rgba(255,255,255,0.1)' }} />
            <div style={{ position:'absolute', bottom:4, left:4, background:'rgba(0,0,0,0.5)', borderRadius:4, padding:'1px 5px', fontSize:'0.55rem', color:'#fff' }}>🔍</div>
          </div>
        );
        return (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'rgba(255,255,255,0.08)', borderRadius:'0.45rem', padding:'0.3rem 0.5rem', cursor:'pointer' }} onClick={() => onPreview(att)}>
            <span style={{ flexShrink:0, opacity:0.6 }}>{FILE_ICON}</span>
            <span style={{ flex:1, fontSize:'0.72rem', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{att.name}</span>
            <a href={att.data_url} download={att.name} title="تحميل" onClick={e => e.stopPropagation()} style={{ flexShrink:0, color:'#0ABAB5', lineHeight:1, display:'flex' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </a>
          </div>
        );
      })}
    </div>
  );
}

function useChatTheme() {
  const [dark, setDark] = useState(true);
  useEffect(() => {
    const check = () => setDark(document.documentElement.getAttribute('data-theme') !== 'light');
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

const fmtTime = (d) => {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
};

const STYLE = `
  @keyframes fc-bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }

  @keyframes fc-in {
    0%   { opacity:0; transform:translateY(40px) scale(0.94); filter:blur(6px); }
    55%  { opacity:1; filter:blur(0); }
    100% { opacity:1; transform:translateY(0)    scale(1);    filter:blur(0); }
  }
  @keyframes fc-out {
    0%   { opacity:1; transform:translateY(0)    scale(1);    filter:blur(0); }
    100% { opacity:0; transform:translateY(30px) scale(0.95); filter:blur(5px); }
  }

  @keyframes fc-ripple {
    0%   { box-shadow: 0 0 0 0 rgba(10,186,181,0.55); }
    70%  { box-shadow: 0 0 0 14px rgba(10,186,181,0); }
    100% { box-shadow: 0 0 0 0   rgba(10,186,181,0); }
  }
  @keyframes fc-glow-btn {
    0%,100% { box-shadow: 0 4px 18px rgba(10,186,181,0.35), 0 2px 6px rgba(0,0,0,0.3); }
    50%     { box-shadow: 0 4px 28px rgba(10,186,181,0.65), 0 2px 8px rgba(0,0,0,0.3); }
  }
  @keyframes fc-msg-in { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:none} }
  @keyframes fc-pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.7)} }
  @keyframes fc-lb-in { from{opacity:0;transform:scale(0.94)} to{opacity:1;transform:scale(1)} }

  .fc-win-in  { animation: fc-in  0.42s cubic-bezier(0.16,1,0.3,1) forwards; }
  .fc-win-out { animation: fc-out 0.22s cubic-bezier(0.4,0,1,1)    forwards; }
  .fc-btn-idle { animation: fc-glow-btn 3s ease-in-out infinite; }
  .fc-btn-new  { animation: fc-bounce 1.1s ease-in-out infinite, fc-ripple 1.4s ease-out infinite, fc-glow-btn 1.5s ease-in-out infinite; }
  .fc-msg { animation: fc-msg-in 0.2s ease; }
  .fc-attach-btn:hover { background: rgba(10,186,181,0.15) !important; }
`;

export default function FloatingChat() {
  const { getToken, customer } = useCustomerAuth();
  const isDark = useChatTheme();

  const [open, setOpen]               = useState(false);
  const [closing, setClosing]         = useState(false);
  const [view, setView]               = useState('chat'); // 'list' | 'chat'
  const [session, setSession]         = useState(null);
  const [allSessions, setAllSessions] = useState([]);
  const [messages, setMessages]       = useState([]);
  const [text, setText]               = useState('');
  const [sending, setSending]         = useState(false);
  const [loading, setLoading]         = useState(false);
  const [unread, setUnread]           = useState(0);
  const [hasNew, setHasNew]           = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [lightbox, setLightbox]       = useState(null);
  const [toast, setToast]             = useState(null);

  const bottomRef   = useRef(null);
  const inputRef    = useRef(null);
  const fileRef     = useRef(null);
  const prevMsgLen  = useRef(0);
  const pollRef     = useRef(null);

  const showToast = useCallback((msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const authHeaders = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  }, [getToken]);

  const [confirmDelete, setConfirmDelete] = useState(null); // session id to confirm

  const deleteSession = useCallback(async (sid) => {
    try {
      const token = getToken();
      const res = await fetch(`${CHAT_API}/customer/session/${sid}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        setAllSessions(prev => prev.filter(s => s.id !== sid));
        if (session?.id === sid) { setSession(null); setMessages([]); }
      }
    } catch {}
    setConfirmDelete(null);
  }, [getToken, session]);

  const fetchAllSessions = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${CHAT_API}/customer/sessions`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const data = await res.json();
      setAllSessions(data.sessions || []);
    } catch {}
  }, [getToken]);

  const openSessionById = useCallback(async (sid) => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${CHAT_API}/customer/session/${sid}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const data = await res.json();
      setSession(data.session || null);
      setMessages(data.messages || []);
      prevMsgLen.current = (data.messages || []).length;
      setView('chat');
    } catch {} finally { setLoading(false); }
  }, [getToken]);

  const fetchSession = useCallback(async (silent = false, markRead = false) => {
    if (!silent) setLoading(true);
    try {
      const url = markRead ? `${CHAT_API}/customer/session?mark_read=true` : `${CHAT_API}/customer/session`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setSession(data.session || null);
      const msgs = data.messages || [];
      // Count unread from DB (not cumulative) — admin msgs with no read_at
      const unreadCount = msgs.filter(m => m.sender_type !== 'customer' && !m.read_at).length;
      if (!open) {
        if (unreadCount > 0) { setUnread(unreadCount); setHasNew(true); }
        else { setUnread(0); setHasNew(false); }
      }
      prevMsgLen.current = msgs.length;
      setMessages(msgs);
    } catch {}
    finally { if (!silent) setLoading(false); }
  }, [authHeaders, open]);

  const viewRef = useRef(view);
  const openRef = useRef(open);
  useEffect(() => { viewRef.current = view; }, [view]);
  useEffect(() => { openRef.current = open; }, [open]);

  // Initial load + polling every 4s
  useEffect(() => {
    if (!customer) return;
    fetchSession();
    pollRef.current = setInterval(() => {
      if (document.hidden) return;
      if (openRef.current && viewRef.current === 'list') {
        fetchAllSessions();
      } else if (openRef.current && viewRef.current === 'chat') {
        fetchSession(true, true); // mark as read when actively viewing chat
      } else {
        fetchSession(true, false); // background poll — don't mark read
      }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [customer, fetchSession, fetchAllSessions]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setHasNew(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
      inputRef.current?.focus();
    }
  }, [open, messages.length]);

  const closeChat = () => {
    setClosing(true);
    setTimeout(() => { setOpen(false); setClosing(false); }, 260);
  };

  const openChat = async () => {
    setOpen(true);
    setClosing(false);
    setUnread(0);
    setHasNew(false);
    // Refresh session + sessions list together
    const [sessRes, allRes] = await Promise.all([
      fetch(`${CHAT_API}/customer/session`, { headers: authHeaders() }),
      fetch(`${CHAT_API}/customer/sessions`, { headers: authHeaders() }),
    ]);
    if (sessRes.ok) {
      const d = await sessRes.json();
      setSession(d.session || null);
      setMessages(d.messages || []);
      prevMsgLen.current = (d.messages || []).length;
    }
    if (allRes.ok) {
      const d = await allRes.json();
      setAllSessions(d.sessions || []);
    }
    setView('list');
  };

  const startNewSession = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${CHAT_API}/customer/session`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({}),
      });
      if (res.ok) {
        await fetchSession();
        setView('chat');
      }
    } catch {} finally { setLoading(false); }
  };

  const sendMsg = async () => {
    const msg = text.trim();
    const hasFiles = pendingFiles.length > 0;
    if (!msg && !hasFiles) return;
    if (sending) return;

    // Security: scan text
    if (msg) {
      const textCheck = scanMessageText(msg);
      if (!textCheck.safe) { showToast(textCheck.reason); return; }
      if (textCheck.warning) showToast(textCheck.warning, 'warn');
    }

    setSending(true);
    setText('');
    const files = [...pendingFiles];
    setPendingFiles([]);

    try {
      if (hasFiles) {
        // Validate files client-side
        const check = await validateFiles(files);
        if (!check.safe) { showToast(check.reason); setSending(false); return; }

        const form = new FormData();
        if (msg) form.append('message', msg);
        files.forEach(f => form.append('files', f));
        const token = getToken();
        await fetch(`${CHAT_API}/customer/session/upload`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: form,
        });
      } else {
        const tmp = { id: 'tmp_' + Date.now(), sender_type: 'customer', message: msg, attachments: [], created_at: new Date().toISOString() };
        setMessages(prev => [...prev, tmp]);
        const res = await fetch(`${CHAT_API}/customer/session/message`, {
          method: 'POST', headers: authHeaders(), body: JSON.stringify({ message: msg }),
        });
        if (!res.ok) setMessages(prev => prev.filter(m => m.id !== tmp.id));
      }
      await fetchSession(true);
    } catch { showToast('فشل إرسال الرسالة'); }
    finally { setSending(false); }
  };

  const onPickFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    const check = await validateFiles(picked);
    if (!check.safe) { showToast(check.reason); return; }
    setPendingFiles(prev => [...prev, ...picked].slice(0, 5));
  };

  const endSession = async () => {
    if (!window.confirm('هل تريد إنهاء المحادثة؟')) return;
    try {
      await fetch(`${CHAT_API}/customer/session/close`, { method: 'POST', headers: authHeaders(), body: '{}' });
      setSession(null); setMessages([]); prevMsgLen.current = 0; setOpen(false);
    } catch {}
  };

  if (!customer) return null;

  // Colors
  const bg      = isDark ? '#061828' : '#ffffff';
  const surface = isDark ? 'rgba(8,28,48,0.95)' : '#f8fafc';
  const border  = isDark ? 'rgba(34,211,238,0.18)' : 'rgba(10,186,181,0.2)';
  const textC   = isDark ? 'rgba(240,249,255,0.95)' : '#1e3a4a';
  const muted   = isDark ? 'rgba(186,230,253,0.5)'  : '#64748b';
  const inputBg = isDark ? 'rgba(3,15,28,0.6)'      : '#f1f5f9';

  const imgSrc = isDark
    ? '/assets/images/sysIcons/live-chat_withBlackGround.png'
    : '/assets/images/sysIcons/live-chat_withWhiteGround.png';

  const statusLabel = !session ? 'نحن هنا للمساعدة' : session.status === 'active' ? 'متصل' : session.status === 'waiting' ? 'سيرد عليك قريباً' : 'مغلق';
  const statusColor = !session ? muted : session.status === 'active' ? '#10b981' : '#94a3b8';

  return (
    <>
      <style>{STYLE}</style>

      {/* File Preview Modal */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position:'fixed', inset:0, zIndex:99999,
            background:'rgba(0,0,0,0.92)',
            display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
          }}
        >
          {/* Header */}
          <div onClick={e => e.stopPropagation()} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'min(92vw,820px)', padding:'0.5rem 0.75rem', background:'rgba(255,255,255,0.07)', borderRadius:'0.75rem 0.75rem 0 0', gap:'0.5rem' }}>
            <span style={{ color:'#fff', fontSize:'0.8rem', fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>{lightbox.name}</span>
            <a href={lightbox.data_url} download={lightbox.name} title="تحميل" style={{ color:'#0ABAB5', display:'flex', alignItems:'center', gap:'0.25rem', fontSize:'0.75rem', fontWeight:700, textDecoration:'none', flexShrink:0 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              تحميل
            </a>
            <button onClick={() => setLightbox(null)} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'50%', width:30, height:30, color:'#fff', fontSize:'1rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>✕</button>
          </div>
          {/* Content */}
          <div onClick={e => e.stopPropagation()} style={{ width:'min(92vw,820px)', background:'#111', borderRadius:'0 0 0.75rem 0.75rem', overflow:'hidden', maxHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
            {lightbox.type?.startsWith('image/')
              ? <img src={lightbox.data_url} alt={lightbox.name} style={{ maxWidth:'100%', maxHeight:'80vh', objectFit:'contain', display:'block', animation:'fc-lb-in 0.18s ease' }} />
              : <iframe src={lightbox.data_url} title={lightbox.name} style={{ width:'100%', height:'75vh', border:'none', display:'block' }} />
            }
          </div>
        </div>
      )}

      {/* Floating Button */}
      {!open && (
        <button
          onClick={openChat}
          title="الدردشة المباشرة"
          className={hasNew ? 'fc-btn-new' : 'fc-btn-idle'}
          style={{
            position: 'fixed', bottom: 100, left: 16, zIndex: 9998,
            width: 42, height: 42, borderRadius: '50%',
            border: 'none', cursor: 'pointer', padding: 0,
            background: 'transparent',
            transition: 'transform 0.18s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <AgentAvatarBtn isDark={isDark} />
          {unread > 0 && (
            <span style={{
              position: 'absolute', top: -4, right: -4,
              background: '#ef4444', color: '#fff',
              borderRadius: '50%', width: 20, height: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.65rem', fontWeight: 800,
              boxShadow: '0 0 0 2px ' + bg,
              animation: 'fc-pulse-dot 1.4s ease-in-out infinite',
            }}>{unread}</span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div className={closing ? 'fc-win-out' : 'fc-win-in'} style={{
          position: 'fixed', bottom: 24, left: 24, zIndex: 9999,
          width: 340, height: 500,
          borderRadius: '1.25rem',
          background: surface,
          border: `1.5px solid ${border}`,
          boxShadow: isDark
            ? '0 24px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(10,186,181,0.12)'
            : '0 20px 60px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          isolation: 'isolate',
          fontFamily: "'Cairo', sans-serif",
          direction: 'rtl',
        }}>

          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.65rem 0.9rem',
            background: isDark ? 'rgba(10,186,181,0.08)' : 'rgba(10,186,181,0.06)',
            borderBottom: `1px solid ${border}`,
            flexShrink: 0,
          }}>
            {view === 'chat' && (
              <button onClick={() => { setView('list'); fetchAllSessions(); }} title="المحادثات" style={{ background:'none', border:'none', cursor:'pointer', color:muted, padding:'0.2rem', borderRadius:'0.4rem', display:'flex', alignItems:'center', flexShrink:0 }}>{BACK_ICON}</button>
            )}
            <div style={{ width:32, height:32, borderRadius:'50%', overflow:'hidden', flexShrink:0 }}>{AGENT_AVATAR}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: '0.8rem', color: textC, lineHeight: 1.2 }}>
                {view === 'chat' ? 'الدعم الفني' : 'محادثاتي'}
              </div>
              {view === 'chat' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.05rem' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.65rem', color: statusColor, fontWeight: 700 }}>{statusLabel}</span>
                </div>
              )}
            </div>
            <button onClick={closeChat} title="تصغير" style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, padding: '0.2rem', borderRadius: '0.4rem', display: 'flex', alignItems: 'center' }}>{MIN_ICON}</button>
            <button onClick={closeChat} title="إغلاق" style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, padding: '0.2rem', borderRadius: '0.4rem', display: 'flex', alignItems: 'center' }}>{CLOSE_ICON}</button>
          </div>

          {/* Toast */}
          {toast && (
            <div style={{
              position:'absolute', top:56, left:'50%', transform:'translateX(-50%)',
              background: toast.type === 'warn' ? '#92400e' : '#7f1d1d',
              color:'#fff', fontSize:'0.7rem', fontWeight:700,
              padding:'0.35rem 0.8rem', borderRadius:'0.5rem',
              whiteSpace:'nowrap', zIndex:10, boxShadow:'0 4px 12px rgba(0,0,0,0.3)',
            }}>{toast.msg}</div>
          )}

          {/* === Sessions List View === */}
          {view === 'list' && (
            <div style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column' }}>
              {loading ? (
                <div style={{ textAlign:'center', color:muted, fontSize:'0.75rem', marginTop:'2rem' }}>جاري التحميل...</div>
              ) : (
                <>
                  {allSessions.length === 0 ? (
                    <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'2rem 1.5rem', gap:'0.55rem' }}>
                      <div style={{
                        width:72, height:72, borderRadius:'50%',
                        background: isDark ? 'rgba(10,186,181,0.12)' : 'rgba(10,186,181,0.08)',
                        border:'1.5px solid rgba(10,186,181,0.25)',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        marginBottom:'0.25rem',
                        filter:'drop-shadow(0 4px 14px rgba(10,186,181,0.2))',
                      }}>
                        <Coffee size={34} color="#0ABAB5" strokeWidth={1.5}/>
                      </div>
                      <div style={{ fontWeight:900, fontSize:'1.05rem', color:textC, lineHeight:1.5 }}>
                        يا هلا يا {customer?.display_name?.split(' ')[0] || 'غالي'} 🤍
                      </div>
                      <div style={{ fontSize:'0.74rem', color:muted, lineHeight:2, maxWidth:215 }}>
                        حياك الله، نورك سبقك ✨<br/>
                        نخدمك ونفرح فيك، قول وش تبي وإحنا حاضرين 💙
                      </div>
                    </div>
                  ) : (
                    allSessions.map(s => {
                      const statusBadge = s.status === 'active' ? { bg:'rgba(16,185,129,0.15)', color:'#10b981', label:'نشطة' }
                        : s.status === 'waiting' ? { bg:'rgba(245,158,11,0.12)', color:'#f59e0b', label:'انتظار' }
                        : { bg:'rgba(148,163,184,0.12)', color:'#94a3b8', label:'مغلقة' };
                      const hasUnread = s.unread_count > 0;
                      return (
                        <div key={s.id}
                          style={{
                            display:'flex', alignItems:'center', gap:'0.5rem',
                            padding:'0.65rem 0.9rem',
                            borderBottom:`1px solid ${border}`,
                            transition:'background 0.12s',
                            background: hasUnread
                              ? isDark ? 'rgba(10,186,181,0.09)' : 'rgba(10,186,181,0.06)'
                              : 'transparent',
                            position:'relative',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}
                          onMouseLeave={e => e.currentTarget.style.background = hasUnread ? (isDark ? 'rgba(10,186,181,0.09)' : 'rgba(10,186,181,0.06)') : 'transparent'}
                        >
                          {/* Unread indicator dot */}
                          <div style={{ position:'relative', flexShrink:0 }}>
                            <div style={{ width:38, height:38, borderRadius:'50%', overflow:'hidden', cursor:'pointer' }} onClick={() => openSessionById(s.id)}>{AGENT_AVATAR}</div>
                            {hasUnread && (
                              <span style={{ position:'absolute', top:0, right:0, width:11, height:11, borderRadius:'50%', background:'#0ABAB5', border:`2px solid ${isDark ? '#0d1b2a' : '#fff'}`, display:'block' }}/>
                            )}
                          </div>
                          <div style={{ flex:1, minWidth:0, cursor:'pointer' }} onClick={() => openSessionById(s.id)}>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.3rem' }}>
                              <span style={{ fontSize:'0.75rem', fontWeight: hasUnread ? 900 : 700, color: hasUnread ? (isDark ? '#fff' : '#0f172a') : textC, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                {s.subject || 'دردشة دعم'}
                              </span>
                              <span style={{ fontSize:'0.58rem', color: hasUnread ? '#0ABAB5' : muted, fontWeight: hasUnread ? 700 : 400, flexShrink:0 }}>
                                {s.last_message_at ? fmtTime(s.last_message_at) : ''}
                              </span>
                            </div>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'0.15rem' }}>
                              <span style={{ fontSize:'0.68rem', color: hasUnread ? (isDark ? 'rgba(255,255,255,0.75)' : '#334155') : muted, fontWeight: hasUnread ? 600 : 400, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'70%' }}>
                                {s.last_message || 'لا توجد رسائل بعد'}
                              </span>
                              <div style={{ display:'flex', alignItems:'center', gap:'0.25rem', flexShrink:0 }}>
                                <span style={{ fontSize:'0.58rem', background:statusBadge.bg, color:statusBadge.color, borderRadius:4, padding:'0 5px', fontWeight:700 }}>{statusBadge.label}</span>
                                {hasUnread && (
                                  <span style={{ background:'#0ABAB5', color:'#fff', borderRadius:'50%', minWidth:18, height:18, fontSize:'0.6rem', fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px' }}>{s.unread_count}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* Delete button */}
                          <button
                            onClick={e => { e.stopPropagation(); setConfirmDelete(s.id); }}
                            title="حذف"
                            style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', opacity:0.6, padding:'0.2rem', borderRadius:'0.3rem', display:'flex', alignItems:'center', flexShrink:0, transition:'opacity 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.opacity='1'}
                            onMouseLeave={e => e.currentTarget.style.opacity='0.6'}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                          </button>
                        </div>
                      );
                    })
                  )}
                  <div style={{ height: 60, flexShrink:0 }} />
                </>
              )}
            </div>
          )}

          {/* Confirm delete dialog */}
          {confirmDelete && (
            <div style={{ position:'absolute', inset:0, zIndex:20, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'1.25rem' }}>
              <div style={{ background: isDark ? '#0d2233' : '#fff', border:`1px solid ${border}`, borderRadius:'1rem', padding:'1.25rem 1rem', width:240, textAlign:'center', boxShadow:'0 8px 32px rgba(0,0,0,0.3)' }}>
                <div style={{ fontSize:'1.6rem', marginBottom:'0.4rem' }}>🗑️</div>
                <div style={{ fontWeight:800, fontSize:'0.85rem', color:textC, marginBottom:'1rem' }}>حذف المحادثة؟</div>
                <div style={{ display:'flex', gap:'0.5rem', justifyContent:'center' }}>
                  <button onClick={() => setConfirmDelete(null)} style={{ flex:1, padding:'0.45rem', borderRadius:'0.6rem', border:`1px solid ${border}`, background:'transparent', color:textC, fontFamily:"'Cairo',sans-serif", fontWeight:700, fontSize:'0.78rem', cursor:'pointer' }}>إلغاء</button>
                  <button onClick={() => deleteSession(confirmDelete)} style={{ flex:1, padding:'0.45rem', borderRadius:'0.6rem', border:'none', background:'linear-gradient(135deg,#ef4444,#dc2626)', color:'#fff', fontFamily:"'Cairo',sans-serif", fontWeight:700, fontSize:'0.78rem', cursor:'pointer' }}>حذف</button>
                </div>
              </div>
            </div>
          )}

          {/* New chat FAB — floats above list */}
          {view === 'list' && (
            <button
              onClick={startNewSession}
              title="محادثة جديدة"
              style={{
                position: 'absolute', bottom: 14, left: 14, zIndex: 10,
                width: 48, height: 48, borderRadius: '50%', border: 'none',
                background: 'linear-gradient(135deg,#0ABAB5,#0369a1)',
                boxShadow: '0 4px 18px rgba(10,186,181,0.45)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform='scale(1.08)'; e.currentTarget.style.boxShadow='0 6px 24px rgba(10,186,181,0.6)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.boxShadow='0 4px 18px rgba(10,186,181,0.45)'; }}
            >
              <svg viewBox="0 0 24 24" fill="none" width="24" height="24">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill="rgba(255,255,255,0.2)" stroke="white" strokeWidth="1.8"/>
                <line x1="12" y1="8" x2="12" y2="16" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
                <line x1="8" y1="12" x2="16" y2="12" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
              </svg>
            </button>
          )}

          {/* Watermark logo — centered on the whole chat window */}
          {view === 'chat' && (
            <img
                src={isDark ? '/assets/images/logo/white-logo.png' : '/assets/images/logo/blue-logo.png'}
              alt=""
              style={{
                position: 'absolute',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 300, height: 300,
                objectFit: 'contain',
                opacity: isDark ? 0.05 : 0.07,
                pointerEvents: 'none',
                userSelect: 'none',
                zIndex: 0,
              }}
            />
          )}

          {/* === Chat View === */}
          {view === 'chat' && (
          <div style={{
            flex: 1, overflowY: 'auto', padding: '0.75rem',
            display: 'flex', flexDirection: 'column', gap: '0.5rem',
            position: 'relative',
          }}>
            {loading && messages.length === 0 ? (
              <div style={{ textAlign: 'center', color: muted, fontSize: '0.75rem', marginTop: '2rem' }}>جاري التحميل...</div>
            ) : messages.length === 0 ? (
              <div style={{ textAlign: 'center', color: muted, fontSize: '0.75rem', marginTop: '2rem', lineHeight: 1.7 }}>
                <div style={{ width:52, height:52, borderRadius:'50%', overflow:'hidden', margin:'0 auto 0.5rem', opacity:0.75 }}>{AGENT_AVATAR}</div>
                <br />مرحباً! كيف يمكننا مساعدتك؟<br />
                <span style={{ fontSize: '0.65rem', opacity: 0.6 }}>سيرد فريق الدعم في أقرب وقت</span>
              </div>
            ) : (
              messages.map((msg) => {
                const isMine = msg.sender_type === 'customer';
                const atts = msg.attachments || [];
                const isRead = !!msg.read_at;
                return (
                  <div key={msg.id} className="fc-msg" style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', position:'relative', zIndex:1 }}>
                    <div style={{
                      maxWidth: '82%',
                      background: isMine
                        ? 'linear-gradient(135deg,#0ABAB5,#0891b2)'
                        : isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0',
                      color: isMine ? '#fff' : textC,
                      borderRadius: isMine ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                      padding: '0.45rem 0.7rem',
                      fontSize: '0.78rem', fontWeight: 500,
                      lineHeight: 1.55, wordBreak: 'break-word',
                    }}>
                      {!isMine && <div style={{ fontSize:'0.6rem', color:'#0ABAB5', fontWeight:800, marginBottom:'0.1rem' }}>فريق الدعم</div>}
                      {msg.message && <div style={{ whiteSpace:'pre-wrap' }}>{renderText(msg.message)}</div>}
                      {renderAttachments(atts, att => setLightbox(att))}
                      <div style={{ fontSize:'0.55rem', textAlign: isMine ? 'left' : 'right', marginTop:'0.2rem', display:'flex', alignItems:'center', justifyContent: isMine ? 'flex-end' : 'flex-start', gap:'0.25rem' }}>
                        <span style={{ opacity:0.65 }}>{fmtTime(msg.created_at)}</span>
                        {isMine && (
                          <span style={{ color: isRead ? '#7ffff4' : 'rgba(255,255,255,0.85)', display:'flex', alignItems:'center' }}>
                            {isRead ? CHECK_TWO : CHECK_ONE}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>
          )}

          {/* Pending files preview — only in chat view */}
          {view === 'chat' && pendingFiles.length > 0 && (
            <div style={{ padding:'0.4rem 0.75rem 0', display:'flex', gap:'0.4rem', flexWrap:'wrap', borderTop:`1px solid ${border}` }}>
              {pendingFiles.map((f, i) => (
                <div key={i} style={{ position:'relative', display:'flex', alignItems:'center', gap:'0.25rem', background:'rgba(10,186,181,0.1)', borderRadius:'0.5rem', padding:'0.2rem 0.5rem', fontSize:'0.68rem', color:textC }}>
                  {f.type.startsWith('image/')
                    ? <img src={URL.createObjectURL(f)} alt="" style={{ width:24, height:24, borderRadius:4, objectFit:'cover' }} />
                    : <span style={{ opacity:0.6 }}>{FILE_ICON}</span>
                  }
                  <span style={{ maxWidth:60, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
                  <button onClick={() => setPendingFiles(p => p.filter((_,j)=>j!==i))} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:'0 2px', lineHeight:1, fontSize:'0.75rem' }}>✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Input — only in chat view */}
          {view === 'chat' && <div style={{
            padding: '0.6rem 0.75rem',
            borderTop: `1px solid ${border}`,
            display: 'flex', gap: '0.4rem', alignItems: 'center',
            background: isDark ? 'rgba(3,15,28,0.4)' : '#f8fafc',
            flexShrink: 0,
          }}>
            <input type="file" ref={fileRef} multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" style={{ display:'none' }} onChange={onPickFiles} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={!session}
              className="fc-attach-btn"
              title="إرفاق ملف"
              style={{
                width:32, height:32, borderRadius:'50%', border:'none',
                background:'transparent', color: muted,
                cursor: session ? 'pointer' : 'not-allowed',
                display:'flex', alignItems:'center', justifyContent:'center',
                flexShrink:0, transition:'background 0.15s',
              }}
            >{ATTACH_ICON}</button>
            <input
              ref={inputRef}
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMsg(); } }}
              placeholder="اكتب رسالتك..."
              disabled={sending || !session}
              style={{
                flex: 1, border: `1.5px solid ${border}`,
                background: inputBg, color: textC,
                borderRadius: '0.75rem', padding: '0.45rem 0.65rem',
                fontSize: '0.8rem', outline: 'none',
                fontFamily: "'Cairo', sans-serif",
                transition: 'border-color 0.15s',
              }}
              onFocus={e => e.target.style.borderColor = 'rgba(10,186,181,0.6)'}
              onBlur={e => e.target.style.borderColor = border}
            />
            <button
              onClick={sendMsg}
              disabled={(!text.trim() && pendingFiles.length === 0) || sending || !session}
              style={{
                width: 36, height: 36, borderRadius: '50%', border: 'none',
                background: ((!text.trim() && pendingFiles.length === 0) || sending || !session) ? 'rgba(10,186,181,0.25)' : 'linear-gradient(135deg,#0ABAB5,#0891b2)',
                color: '#fff', cursor: ((!text.trim() && !pendingFiles.length) || !session) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'background 0.2s',
              }}
            >{SEND_ICON}</button>
          </div>}

        </div>
      )}
    </>
  );
}
